import type {
    TransactionAmountComparator,
    TransactionFlow,
    TransactionMatchStatus,
    SortDirection,
    TransactionSortField,
} from "../../../../../generated/enums.ts";
import {
    TransactionAmountComparatorEnum,
    TransactionFlowFilterEnum,
    TransactionMatchFilterEnum,
} from "../../../../../types/enums.ts";
import type {TransactionFilters} from "./partnerTransactions.ts";

export interface PartnerTransactionsParams {
    client_business_id?: string;
    bank_account_id?: string;
    date_from?: string;
    date_to?: string;
    flow?: TransactionFlow;
    amount_cents?: number;
    amount_comparator?: TransactionAmountComparator;
    relation_iban?: string;
    general_ledger_account_id?: string;
    match_status?: TransactionMatchStatus;
    search?: string;
    sort: TransactionSortField;
    direction: SortDirection;
}

export const DEFAULT_SORT: TransactionSortField = 'date';
export const DEFAULT_DIRECTION: SortDirection = 'desc';

export const SORT_FIELD_TO_COLUMN: Record<TransactionSortField, string> = {
    date: 'bookingDate',
    amount: 'value',
    relation: 'relationName',
};

export const SORTABLE_COLUMNS: ReadonlyArray<string> = Object.values(SORT_FIELD_TO_COLUMN);

const MATCH_STATUS_TO_PARAM: Record<TransactionMatchFilterEnum, TransactionMatchStatus | undefined> = {
    [TransactionMatchFilterEnum.ANY]: undefined,
    [TransactionMatchFilterEnum.MATCHED]: 'matched',
    [TransactionMatchFilterEnum.UNMATCHED]: 'unmatched',
    [TransactionMatchFilterEnum.BOOKED_AWAY]: 'booked_away',
};

const AMOUNT_COMPARATOR_TO_PARAM: Record<TransactionAmountComparatorEnum, TransactionAmountComparator> = {
    [TransactionAmountComparatorEnum.GT]: 'gt',
    [TransactionAmountComparatorEnum.LT]: 'lt',
};

export function isSortField(value: string | null): value is TransactionSortField {
    return value !== null && Object.prototype.hasOwnProperty.call(SORT_FIELD_TO_COLUMN, value);
}

export function isSortDirection(value: string | null): value is SortDirection {
    return value === 'asc' || value === 'desc';
}

export function columnToSortField(column: string): TransactionSortField | null {
    const entry = Object.entries(SORT_FIELD_TO_COLUMN).find(([, columnName]) => columnName === column);
    return entry ? entry[0] as TransactionSortField : null;
}

export function toPartnerTransactionsParams(
    filters: TransactionFilters,
    sort: TransactionSortField,
    direction: SortDirection,
): PartnerTransactionsParams {
    const params: PartnerTransactionsParams = {sort, direction};

    if (filters.clientId) params.client_business_id = filters.clientId;
    if (filters.bankAccountId) params.bank_account_id = filters.bankAccountId;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.flow && filters.flow !== TransactionFlowFilterEnum.ANY) params.flow = filters.flow;
    if (filters.amount) {
        params.amount_cents = Math.round(filters.amount * 100);
        params.amount_comparator = AMOUNT_COMPARATOR_TO_PARAM[
            filters.amountComparator ?? TransactionAmountComparatorEnum.GT
        ];
    }
    if (filters.relationIban) params.relation_iban = filters.relationIban;
    if (filters.generalLedgerAccountId) params.general_ledger_account_id = filters.generalLedgerAccountId;
    const matchStatus = filters.matchStatus ? MATCH_STATUS_TO_PARAM[filters.matchStatus] : undefined;
    if (matchStatus) params.match_status = matchStatus;
    if (filters.search) params.search = filters.search;

    return params;
}
